import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { SeoService } from "../../../../shared/services/seo.service";
import { BlogService } from "../../../../shared/services/blog.service";
import {
  NavbarComponent,
  NavbarTypeEnum,
} from "../../components/navbar/navbar.component";
import { FooterComponent } from "../../components/footer/footer.component";
import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from "../../../../shared/ui/breadcrumb/breadcrumb.component";
import { BlogCardComponent } from "./blog-card.component";
import {
  BlogListResponse,
  BlogPostSummary,
  BlogSectionKey,
  SECTION_LABELS,
} from "./blog.models";

/**
 * Listado de una sección del blog (`/guias`, `/recetas`, …). La página 1 se
 * pre-carga vía blogSectionResolver (SSR + SPA nav sin flash). Las páginas 2+
 * se cargan client-side con "Cargar más".
 */
@Component({
  selector: "app-blog-section",
  imports: [
    RouterLink,
    NavbarComponent,
    FooterComponent,
    BreadcrumbComponent,
    BlogCardComponent,
  ],
  host: { "[class.reveals-enabled]": "revealsEnabled()" },
  styles: [
    `
      :host(.reveals-enabled) [data-reveal] {
        opacity: 0;
        transform: translateY(20px);
        transition:
          opacity 0.55s ease-out,
          transform 0.55s ease-out;
      }
      :host(.reveals-enabled) [data-reveal].is-visible {
        opacity: 1;
        transform: none;
      }
      @media (prefers-reduced-motion: reduce) {
        :host(.reveals-enabled) [data-reveal] {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ],
  template: `
    <app-navbar [type]="navbarTypeEnum.LANDING" [isCheckout]="false" />

    <section
      class="body-font relative flex flex-col items-center justify-center"
    >
      <div
        class="flex flex-wrap justify-center self-center sm:flex-nowrap sm:px-2 sm:py-4"
      >
        <div
          class="relative mx-auto flex overflow-hidden bg-surface shadow-xl sm:rounded-3xl"
        >
          <div class="h-full w-full max-w-7xl">
            <div class="flex h-fit flex-col items-start px-4 py-8 sm:px-16">
              <div class="w-full">
                <header class="mb-10">
                  <ml-breadcrumb [items]="breadcrumbItems" class="mb-6 block" />
                  <h1 class="text-4xl font-extrabold tracking-tight text-fg">
                    {{ label }}.
                  </h1>
                  <!-- <p class="mt-3 max-w-2xl text-fg-muted">
                    Artículos sobre creatina en {{ label.toLowerCase() }}.
                  </p> -->
                </header>

                @if (posts().length > 0) {
                  <div class="grid gap-6 sm:grid-cols-2 grid-cols-1">
                    @for (post of posts(); track post.slug) {
                      <div data-reveal><app-blog-card [post]="post" /></div>
                    }
                  </div>

                  @if (page() < totalPages()) {
                    <div class="mt-10 flex justify-center">
                      <button
                        type="button"
                        (click)="loadMore()"
                        [disabled]="loading()"
                        class="rounded-full border border-border px-6 py-3 text-sm font-semibold text-fg transition hover:border-primary disabled:opacity-50"
                      >
                        {{ loading() ? "Cargando…" : "Cargar más" }}
                      </button>
                    </div>
                  }
                } @else if (empty()) {
                  <div class="py-20 text-center">
                    <h2 class="text-xl font-bold text-fg">
                      Pronto, en {{ label }}
                    </h2>
                    <p class="mt-2 text-fg-muted">
                      Estamos preparando contenido de esta sección.
                    </p>
                    <a
                      routerLink="/blog"
                      class="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-hover"
                      >Volver al blog</a
                    >
                  </div>
                } @else if (error()) {
                  <div class="py-20 text-center">
                    <h2 class="text-xl font-bold text-fg">
                      No pudimos cargar esta sección
                    </h2>
                    <p class="mt-2 text-fg-muted">
                      Probá de nuevo en un momento.
                    </p>
                  </div>
                } @else {
                  <div
                    class="grid animate-pulse gap-6 sm:grid-cols-2 lg:grid-cols-3"
                    aria-hidden="true"
                  >
                    @for (s of skeletons; track s) {
                      <div class="space-y-3">
                        <div class="aspect-[16/10] rounded-xl bg-bg-alt"></div>
                        <div class="h-4 w-2/3 rounded bg-bg-alt"></div>
                        <div class="h-4 w-full rounded bg-bg-alt"></div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <app-footer />
  `,
})
export class BlogSectionComponent implements OnInit {
  protected readonly navbarTypeEnum = NavbarTypeEnum;
  protected readonly skeletons = [1, 2, 3, 4, 5, 6];

  protected readonly posts = signal<BlogPostSummary[]>([]);
  protected readonly page = signal(1);
  protected readonly totalPages = signal(1);
  /** Only true while fetching pages 2+ client-side via "Cargar más". */
  protected readonly loading = signal(false);
  protected readonly empty = signal(false);
  protected readonly error = signal(false);
  protected readonly revealsEnabled = signal(false);

  protected readonly section: BlogSectionKey;
  protected readonly label: string;
  protected readonly breadcrumbItems: BreadcrumbItem[];

  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly blog = inject(BlogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostEl = inject(ElementRef);
  private observer?: IntersectionObserver;

  constructor() {
    this.section = this.route.snapshot.data["section"] as BlogSectionKey;
    this.label = SECTION_LABELS[this.section] ?? "Blog";
    this.breadcrumbItems = [
      { label: "Inicio", link: "/" },
      { label: "Blog", link: "/blog" },
      { label: this.label },
    ];

    afterNextRender(() => {
      this.revealsEnabled.set(true);
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              this.observer?.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.12, rootMargin: "0px 0px -10% 0px" },
      );
      this.observeReveals();
    });
  }

  ngOnInit(): void {
    const url = `https://magrolabs.com/${this.section}`;
    this.seo.setTitle(`${this.label} sobre creatina | Magrolabs`);
    this.seo.setDescription(
      `Artículos de ${this.label.toLowerCase()} sobre creatina monohidratada: dosis, beneficios, recetas, mitos y novedades.`,
    );
    this.seo.setCanonicalURL(url);
    this.seo.setIndexFollow(true);
    this.seo.setStructuredData(
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${this.label} · Magrolabs`,
        url,
      },
      "ld-collection",
    );

    // Seed page-1 data from the resolver (available synchronously).
    const resolved = this.route.snapshot.data[
      "blogSection"
    ] as BlogListResponse | null;
    if (resolved === null) {
      this.error.set(true);
    } else {
      this.posts.set(resolved.posts);
      this.page.set(resolved.page);
      this.totalPages.set(resolved.totalPages);
      const isEmpty = resolved.total === 0;
      this.empty.set(isEmpty);
      if (isEmpty) this.seo.setIndexFollow(false);
      this.scheduleReveals();
    }
  }

  /** Loads the next page client-side ("Cargar más" button). */
  protected loadMore(): void {
    if (!this.loading() && this.page() < this.totalPages()) {
      this.fetchPage(this.page() + 1);
    }
  }

  private fetchPage(page: number): void {
    this.loading.set(true);
    this.blog
      .getList({ section: this.section, page, limit: 12 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.posts.update((prev) => [...prev, ...res.posts]);
          this.page.set(res.page);
          this.totalPages.set(res.totalPages);
          this.loading.set(false);
          this.scheduleReveals();
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  private scheduleReveals(): void {
    if (typeof requestAnimationFrame === "undefined") return; // SSR
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.observeReveals()),
    );
  }

  private observeReveals(): void {
    if (!this.observer) return;
    const root = this.hostEl.nativeElement as HTMLElement;
    root
      .querySelectorAll("[data-reveal]:not(.is-visible)")
      .forEach((node) => this.observer!.observe(node));
  }
}
